// Partners portal reducer.
//
// Holds the partner-side state for the mobile portal: the partner's own row
// (status / branding / limits), the customer roster, the invite list, and
// the population-health findings + last-run timestamp.
//
// Shape mirrors the web frontend's partners reducer (frontend/client/src/
// store/reducers/partners.js) closely enough that re-using component-level
// selectors is straightforward — partner.list / partner.invites map to
// `customers` and `invites` here. The `gates` block is new (returned by
// the /partners/me endpoint server-side) so conditional UI doesn't have
// to reimplement the partner.status === 'active' && partner.population_health
// check on every render.

import {
  SET_PARTNER,
  SET_PARTNER_FETCHING,
  SET_PARTNER_CUSTOMERS,
  SET_PARTNER_PROJECTS,
  SET_PARTNER_INVITES,
  UPSERT_PARTNER_INVITE,
  SET_PARTNER_POPULATION,
  CLEAR_ALL,
} from '../constants';

export interface PartnerRow {
  referral_base: string;
  cognito_id: string;
  account_id: string;
  company_name: string;
  status: 'pending' | 'active' | 'suspended' | 'rejected';
  level: number;
  brand_primary?: string | null;
  brand_secondary?: string | null;
  brand_logo?: string | null;
  /** White-label subdomain (DNS label) → `<subdomain>.eleplan.com`. Assigned
   *  on approval; null for partners approved before the feature shipped. */
  subdomain?: string | null;
  /** Optional copy override for the hero subhead on the co-branded referral
   *  landing page (eleplan.com/<subdomain>). null = render the neutral
   *  default, which states availability through the partner rather than a
   *  recommendation by them. `{name}` is substituted with company_name. */
  landing_subhead?: string | null;
  /** Optional copy override for the "In partnership with <name>" pill above
   *  the landing-page headline. null = render that default. */
  landing_badge?: string | null;
  default_product_id?: string | null;
  default_plans?: number | null;
  default_members?: number | null;
  default_medications?: number | null;
  default_storage_mb?: number | null;
  kb_credits?: number | null;
  max_accounts: number;
  population_health: boolean;
  /** When false, the partner cannot "Access" (switch into) their referred
   *  customer plans — the Access buttons are hidden. Memberships are untouched.
   *  Defaults true (undefined on rows cached before the column shipped = allowed). */
  can_access_plans?: boolean;
  default_discount_duration?: 'forever' | 'once' | 'repeating' | null;
  default_discount_duration_in_months?: number | null;
  default_discount_percent_off?: number | null;
  default_discount_amount_off?: number | null;
  npi_number?: string | null;
  approved_at?: string | null;
  submitted_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface PartnerGates {
  isPending: boolean;
  isActive: boolean;
  isSuspended: boolean;
  canSendInvites: boolean;
  canConvertIntakes: boolean;
  canViewPopulationHealth: boolean;
  brandingLevel: number;
}

interface PartnersState {
  partner: PartnerRow | null;
  gates: PartnerGates | null;
  customers: any[];
  projects: any[];
  invites: any[];
  populationHealth: { findings: any[]; lastUpdated: string | null };
  fetching: {
    partner: boolean;
    customers: boolean;
    projects: boolean;
    invites: boolean;
    populationHealth: boolean;
  };
}

const initialState: PartnersState = {
  partner: null,
  gates: null,
  customers: [],
  projects: [],
  invites: [],
  populationHealth: { findings: [], lastUpdated: null },
  fetching: {
    partner: false,
    customers: false,
    projects: false,
    invites: false,
    populationHealth: false,
  },
};

const partnersReducer = (
  state = initialState,
  { type, payload }: { type: string; payload?: any }
): PartnersState => {
  switch (type) {
    case SET_PARTNER:
      return { ...state, partner: payload?.partner ?? null, gates: payload?.gates ?? null };
    case SET_PARTNER_FETCHING:
      return {
        ...state,
        fetching: { ...state.fetching, [payload.key]: !!payload.value },
      };
    case SET_PARTNER_CUSTOMERS:
      return { ...state, customers: Array.isArray(payload) ? payload : [] };
    case SET_PARTNER_PROJECTS:
      return { ...state, projects: Array.isArray(payload) ? payload : [] };
    case SET_PARTNER_INVITES:
      return { ...state, invites: Array.isArray(payload) ? payload : [] };
    case UPSERT_PARTNER_INVITE: {
      // Replace by id when present, otherwise prepend. Used after send /
      // resend / revoke / convert so the invites list mutates in place
      // without a full refetch.
      if (!payload?.id) return state;
      const idx = state.invites.findIndex((i) => i.id === payload.id);
      if (idx === -1) return { ...state, invites: [payload, ...state.invites] };
      const next = state.invites.slice();
      next[idx] = { ...next[idx], ...payload };
      return { ...state, invites: next };
    }
    case SET_PARTNER_POPULATION:
      return {
        ...state,
        populationHealth: {
          findings: Array.isArray(payload?.findings) ? payload.findings : [],
          lastUpdated: payload?.lastUpdated ?? null,
        },
      };
    case CLEAR_ALL:
      return { ...initialState };
    default:
      return state;
  }
};

export default partnersReducer;
